import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";

import { Disclose } from "@lopie/kernel-ui";

import { askForLessRequest, impactOf } from "./impact.js";
import { joined } from "./SandboxPanel.jsx";
import { refusalWords } from "./sandboxPanel.js";

/**
 * How many people could not open the app as requested, beside what the app asks to be allowed
 * to do (LO-211), on the app's own page and on the chat's request panel.
 *
 * A person opens an app only if they hold every tag it reads or writes with, so what a draft
 * asks for decides who can use it, and the author is told before they send it: "With this access,
 * 3 of 12 people in your org couldn't open it as requested: they lack finance (read Payment)."
 * The count and what people lack are the kernel's own (`GET /manifest-requests/{id}/access-impact`,
 * read as the author), and `impact.js` words them.
 *
 * **Information and never a block.** Nothing here touches Send for approval. It says what widens
 * who can use the app (ask for less access, or split the app) and, on the app's page, offers to
 * put that request in the change box; it does not send it, and it does not hold anything back.
 *
 * **A refusal is information.** A kernel that cannot say (an older one with no such route, or a
 * refusal) is shown in its own words with its status, and the page never makes up a number. The
 * names are the kernel's: listed one click in when its answer carries them, and said not to have
 * been given when it does not.
 */

/** One read of the impact for a draft, again whenever the draft is another one. A response for a
 *  draft the page has since left is dropped. */
export function useAccessImpact(get, requestId, enabled) {
  const [state, setState] = useState({ phase: "idle" });
  const latest = useRef("");
  const load = useCallback(async () => {
    if (!requestId) return;
    latest.current = requestId;
    setState((s) => ({ ...s, phase: "loading", failure: null }));
    try {
      // One read at a time for a draft: a second ask while the first is on its way (a development build
      // mounts an effect twice, and the app's page and the chat's panel can be for one draft) joins it.
      const body = await joined(`impact:${requestId}`, () => get(`/manifest-requests/${requestId}/access-impact`));
      if (latest.current === requestId) setState({ phase: "ready", body });
    } catch (failure) {
      if (latest.current === requestId) setState({ phase: "failed", failure });
    }
  }, [get, requestId]);
  useEffect(() => {
    if (enabled && requestId) load();
    else {
      latest.current = "";
      setState({ phase: "idle" });
    }
  }, [enabled, requestId, load]);
  return { ...state, load, requestId };
}

/** The sentence a person reads, with its glyph in a ring: a glyph and the words say the same
 *  thing, so neither is the colour alone. */
function Said({ glyph, children }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", minWidth: 0 }}>
      <Box component="span" aria-hidden sx={{ flex: "none", fontWeight: 700, width: "1.25em", textAlign: "center", pt: "1px" }}>{glyph}</Box>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>{children}</Typography>
    </Stack>
  );
}

/**
 * `state` is `useAccessImpact`'s. `onAsk(text)` puts what to ask the builder in the box that
 * changes the app (the app's page has one; the chat does not, because the chat is the box).
 */
export function AccessImpact({ state, onAsk }) {
  const id = useId();
  if (!state || state.phase === "idle") return null;
  const heading = <Typography id={id} component="h3" variant="subtitle2">Who could open it</Typography>;
  const frame = (children) => (
    <Box component="section" aria-labelledby={id} sx={{ minWidth: 0, pt: 1.5, mt: 0.5, borderTop: 1, borderColor: "divider" }}>
      <Stack spacing={1}>
        {heading}
        {children}
      </Stack>
    </Box>
  );

  if (state.phase === "loading") {
    return frame(<Typography variant="body2" color="text.secondary" role="status">Counting who could open it…</Typography>);
  }

  const unreadable = (why, said) => frame(
    <>
      <Alert severity="info" variant="outlined" role="status">
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          How many people in your org could open it as requested couldn't be counted just now. {why}
        </Typography>
        {said ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, overflowWrap: "anywhere" }}>
            {said.status ? `status ${said.status}` : "no answer"}{said.code ? <> · <code>{said.code}</code></> : null}
          </Typography>
        ) : null}
      </Alert>
      <Box>
        <Button size="small" variant="outlined" onClick={state.load}>Try again</Button>
      </Box>
      <Typography variant="caption" color="text.secondary">
        This is only to tell you. It doesn't stop the draft being sent.
      </Typography>
    </>,
  );

  if (state.phase === "failed") {
    const said = refusalWords(state.failure);
    return unreadable(said.text || "No reason was given.", said);
  }

  const view = impactOf(state.body);
  if (view.kind === "unreadable") return unreadable("The Kernel's answer had no count of people in it.");

  return frame(
    <>
      <Alert severity={view.kind === "blocked" ? "warning" : "info"} variant="outlined" role="status"
        icon={false} sx={{ "& .MuiAlert-message": { minWidth: 0 } }}>
        <Said glyph={view.kind === "blocked" ? "!" : "✓"}>{view.sentence}</Said>
      </Alert>
      {view.kind === "blocked" ? (
        <>
          {view.names ? (
            <Disclose label="Who they are" count={view.names.filter((n) => !n.more).length} align="row"
              summary="The people the Kernel named to you, and what each lacks.">
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                {view.names.map((n) => (
                  <Typography key={n.name} component="li" variant="body2" sx={{ overflowWrap: "anywhere" }}>
                    {n.name}{n.lacks.length ? <Box component="span" sx={{ color: "text.secondary" }}>{`: lacks ${n.lacks.join(", ")}`}</Box> : null}
                  </Typography>
                ))}
              </Stack>
            </Disclose>
          ) : (
            <Typography variant="caption" color="text.secondary">The Kernel didn't name them to you.</Typography>
          )}
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{view.widen}</Typography>
          {onAsk ? (
            <Box>
              <Button size="small" variant="outlined" onClick={() => onAsk(askForLessRequest(view))}>
                Ask the builder for less access
              </Button>
            </Box>
          ) : null}
        </>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{view.basis}</Typography>
      {view.truncatedLine ? (
        <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>{view.truncatedLine}</Typography>
      ) : null}
      {view.kernelSays.length ? (
        <Disclose label="What this doesn't show" align="row" summary="The Kernel's own sentences about what this count does not cover.">
          <Stack spacing={0.75}>
            {view.kernelSays.map((line) => (
              <Typography key={line} variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{line}</Typography>
            ))}
          </Stack>
        </Disclose>
      ) : null}
    </>,
  );
}
